import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Star, Check, X, Trash2, Flag } from "lucide-react";

const TABS = [
  { k: "pending", label: "En attente" },
  { k: "published", label: "Publiés" },
  { k: "rejected", label: "Refusés" },
  { k: "reported", label: "Signalés" },
];

const Stars = ({ value }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={13} className={i <= value ? "text-amber-500 fill-amber-500" : "text-slate-300"} />
    ))}
  </span>
);

export default function AdminReviews() {
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState(null);

  const load = (t = tab) => {
    const q = t === "reported" ? "?reported=true" : `?status=${t}`;
    api.get(`/admin/reviews${q}`).then((r) => setData(r.data)).catch(() => setData({ items: [], counts: {} }));
  };
  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const moderate = async (rid, action) => {
    try {
      await api.post(`/admin/reviews/${rid}/moderate?action=${action}`);
      toast.success(action === "approve" ? "Avis publié." : "Avis refusé.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
  };

  const remove = async (rid) => {
    if (!window.confirm("Supprimer définitivement cet avis ?")) return;
    try { await api.delete(`/admin/reviews/${rid}`); toast.success("Avis supprimé."); load(); }
    catch { toast.error("Erreur."); }
  };

  return (
    <div data-testid="admin-reviews-page">
      <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2 text-slate-900" data-testid="admin-reviews-title">
        <Star size={22} className="text-amber-500" /> Modération des avis
      </h1>
      <p className="mt-1 text-sm text-slate-500">
        Les avis sont déposés uniquement par des bénéficiaires ayant utilisé une offre. Validez-les avant publication.
      </p>

      <div className="mt-5 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`admin-reviews-tab-${t.k}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                    tab === t.k
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
            {t.label}
            {data?.counts?.[t.k] != null && <span className="ml-1.5 text-xs opacity-70">{data.counts[t.k]}</span>}
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-3">
        {!data ? <div className="text-slate-500">Chargement…</div>
          : data.items.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-8 text-center text-slate-500" data-testid="admin-reviews-empty">
              Aucun avis dans cette catégorie.
            </div>
          ) : data.items.map((r) => (
            <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`admin-review-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Stars value={r.rating} />
                  <span className="font-semibold text-slate-900 text-sm">{r.author_name}</span>
                  <span className="text-xs text-slate-500">· {r.business_name} {r.business_city && `(${r.business_city})`}</span>
                  {r.reported && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">
                      <Flag size={10} /> Signalé
                    </span>
                  )}
                </div>
                <span className="text-[11px] text-slate-400">{(r.created_at || "").slice(0, 10)}</span>
              </div>
              {r.comment && <p className="mt-2 text-sm text-slate-700 whitespace-pre-line">{r.comment}</p>}
              {r.report_reason && <p className="mt-2 text-xs text-rose-700">Motif du signalement : {r.report_reason}</p>}
              {r.pro_reply && (
                <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Réponse du commerçant</div>
                  <p className="mt-1 text-sm text-slate-700 whitespace-pre-line">{r.pro_reply}</p>
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                {r.status !== "published" && (
                  <Button size="sm" onClick={() => moderate(r.id, "approve")} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`admin-review-approve-${r.id}`}>
                    <Check size={13} className="mr-1.5" /> Publier
                  </Button>
                )}
                {r.status !== "rejected" && (
                  <Button size="sm" variant="outline" onClick={() => moderate(r.id, "reject")} data-testid={`admin-review-reject-${r.id}`}>
                    <X size={13} className="mr-1.5" /> Refuser
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-rose-700 hover:bg-rose-50" data-testid={`admin-review-delete-${r.id}`}>
                  <Trash2 size={13} className="mr-1.5" /> Supprimer
                </Button>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
